import { useState, useCallback, useMemo } from 'react';
import {
  ComingSoonData,
  ComingSoonWeek,
  getComingSoonReports,
  subscribeToReport,
  unsubscribeFromReport,
} from '@/api/coming-soon';

interface UseComingSoonProps {
  initialData: ComingSoonData;
  userId?: string;
  culture?: string;
}

export const useComingSoon = ({ initialData, userId, culture }: UseComingSoonProps) => {
  const [reports, setReports] = useState<ComingSoonWeek[]>(() => {
    const data = initialData?.comingSoonByDates?.comingSoonReports || [];
    return data;
  });
  const [selectedDirectories, setSelectedDirectories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const directoryOptions = useMemo(() => {
    if (!initialData?.directorySubscriptions) {
      return [];
    }
    const options = initialData.directorySubscriptions.map((dir) => ({
      label: dir.directoryName,
      value: dir.directoryId,
      count: 0,
      disabled: false,
    }));
    return options;
  }, [initialData?.directorySubscriptions]);

  const handleSubscriptionChange = useCallback(async (reportId: string, isSubscribed: boolean) => {
    try {
      setLoading(true);

      // API call first (like Vue implementation)
      if (isSubscribed) {
        await subscribeToReport(reportId);
      } else {
        await unsubscribeFromReport(reportId);
      }

      // Then update UI state (like Vue emit)
      setReports((prevReports) =>
        prevReports.map((week) => ({
          ...week,
          reports: week.reports.map((report) => (report.reportId === reportId ? { ...report, isSubscribed } : report)),
        })),
      );
    } catch (error) {
      console.error(' Subscription change failed:', error);
      // Revert UI state on error
      setReports((prevReports) =>
        prevReports.map((week) => ({
          ...week,
          reports: week.reports.map((report) =>
            report.reportId === reportId ? { ...report, isSubscribed: !isSubscribed } : report,
          ),
        })),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDirectoryFilter = useCallback(
    async (directories: string[]) => {
      if (!userId || !culture) {
        console.warn('Missing userId or culture for directory filter');
        return;
      }

      setSelectedDirectories(directories);
      setLoading(true);

      try {
        const params = {
          userId,
          culture,
          directoryIds: directories.length > 0 ? directories : undefined,
        };
        const result = await getComingSoonReports(params);
        // Handle different response structures
        const reports = result.comingSoonByDates?.comingSoonReports || [];
        setReports(reports);
      } catch (error) {
        console.error('Directory filter failed:', error);
      } finally {
        setLoading(false);
      }
    },
    [userId, culture],
  );

  return {
    reports,
    selectedDirectories,
    loading,
    directoryOptions,
    handleSubscriptionChange,
    handleDirectoryFilter,
  };
};
export default useComingSoon;
